import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, UserCheck, Clock, Check, MessageSquare, Bookmark, BookmarkCheck, Heart, Rss } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useChatDock } from "@/context/ChatDockContext";
import { useRelationshipStatus, toggleRelationship } from "@/lib/relationshipStore";
import api from "@/lib/api";

export function ConnectButton({ targetUserId, className, size = "sm" }) {
  const [state, setState] = useState(null);
  const [cid, setCid] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/network/status/${targetUserId}`);
      setState(data.interaction_available === false ? "unavailable" : data.state);
      setCid(data.connection_id || null);
    } catch (e) { /* signed-out / no status */ }
  };
  useEffect(() => { if (targetUserId) load(); /* eslint-disable-next-line */ }, [targetUserId]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Action failed"); }
    finally { setBusy(false); }
  };

  if (state === null || state === "self") return null;
  // Platform User Blocking V1 — prohibited interpersonal actions are simply unavailable.
  // The same for both sides of a block, so it never discloses direction.
  if (state === "unavailable") return null;
  if (state === "connected")
    return <Button variant="secondary" size={size} className={className} disabled data-testid="connect-state-connected"><UserCheck className="mr-0.5 h-4 w-4" />Friends</Button>;
  if (state === "pending_outgoing")
    return <Button variant="secondary" size={size} className={className} disabled={busy} onClick={() => act(() => api.delete(`/network/${cid}`), "Request withdrawn")} data-testid="connect-state-pending"><Clock className="mr-0.5 h-4 w-4" />Requested</Button>;
  if (state === "pending_incoming")
    return <Button size={size} className={className} disabled={busy} onClick={() => act(() => api.post("/network/respond", { connection_id: cid, action: "accept" }), "You're now friends")} data-testid="connect-accept"><Check className="mr-0.5 h-4 w-4" />Accept friend</Button>;
  return <Button variant="secondary" size={size} className={className} disabled={busy} onClick={() => act(() => api.post("/network/connect", { target_user_id: targetUserId }), "Friend request sent")} data-testid="connect-button"><UserPlus className="mr-0.5 h-4 w-4" />Friend</Button>;
}

export function MessageButton({ targetUserId, className, size = "sm", variant = "default", label = "Message", iconOnly = false }) {
  const { openChatWithUser } = useChatDock();
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    if (!targetUserId) return undefined;
    let ok = true;
    api.get(`/messages/can-message/${targetUserId}`)
      .then(({ data }) => { if (ok) setAllowed(!!data.can_message); })
      .catch(() => { if (ok) setAllowed(false); });
    return () => { ok = false; };
  }, [targetUserId]);
  const go = async () => {
    setBusy(true);
    try { await openChatWithUser(targetUserId); }
    catch (e) { toast.error("Could not open conversation"); }
    finally { setBusy(false); }
  };
  if (!allowed) return null;
  return (
    <Button variant={variant} size={iconOnly ? "icon" : size} className={className} onClick={go} disabled={busy} title="Message" aria-label="Message" data-testid="message-button">
      <MessageSquare className={iconOnly ? "h-4 w-4" : "mr-1.5 h-4 w-4"} />{!iconOnly && ` ${label}`}
    </Button>
  );
}

export function BookmarkButton({ itemType, itemId, iconOnly, className, size = "sm", variant = "secondary" }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let ok = true;
    api.get(`/bookmarks/check?item_type=${itemType}&item_id=${itemId}`)
      .then(({ data }) => { if (ok) setOn(data.bookmarked); }).catch(() => {});
    return () => { ok = false; };
  }, [itemType, itemId]);
  const toggle = async () => {
    try {
      const { data } = await api.post("/bookmarks", { item_type: itemType, item_id: itemId });
      setOn(data.bookmarked);
      toast.success(data.bookmarked ? "Saved to bookmarks" : "Removed from bookmarks");
    } catch (e) { toast.error("Action failed"); }
  };
  const Icon = on ? BookmarkCheck : Bookmark;
  return (
    <Button variant={on ? "default" : variant} size={iconOnly ? "icon" : size} className={className} onClick={toggle} title="Bookmark" aria-label="Bookmark" data-testid="bookmark-button">
      <Icon className={iconOnly ? "h-4 w-4" : "mr-1.5 h-4 w-4"} />{!iconOnly && (on ? "Saved" : "Bookmark")}
    </Button>
  );
}

/**
 * Triple-F: Fan + Follow toggles for any profile (personal | business | capital).
 * Friend (mutual connect) is handled separately by <ConnectButton/>.
 * Renders nothing while signed-out (status fetch fails) so callers can drop it in safely.
 */
export function FanFollowButtons({ targetType, targetId, order = ["follow", "fan"], size = "sm", className = "", showCount = true, ownerUserId = null }) {
  const st = useRelationshipStatus(targetType, targetId);
  const [available, setAvailable] = useState(true);
  useEffect(() => {
    // Platform User Blocking V1 — only PERSONAL follow/fan is withdrawn for a blocked pair;
    // Business / Capital relationships are never affected by a personal block.
    if (targetType !== "personal" || !ownerUserId) { setAvailable(true); return undefined; }
    let ok = true;
    api.get(`/network/status/${ownerUserId}`)
      .then(({ data }) => { if (ok) setAvailable(data.interaction_available !== false); })
      .catch(() => { if (ok) setAvailable(true); });
    return () => { ok = false; };
  }, [targetType, ownerUserId]);
  const toggle = async (kind) => {
    try {
      await toggleRelationship(targetType, targetId, kind);
    } catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Action failed"); }
  };
  if (!st || !available) return null;
  const one = (kind) => {
    const active = kind === "fan" ? st.fan : st.follow;
    const count = kind === "fan" ? st.fan_count : st.follow_count;
    const Icon = kind === "fan" ? Heart : Rss;
    const label = kind === "fan" ? "Fan" : (active ? "Following" : "Follow");
    return (
      <Button key={kind} variant={active ? "default" : "secondary"} size={size} className={className} onClick={() => toggle(kind)} data-testid={`${kind}-button`}>
        <Icon className={`mr-0.5 h-4 w-4 ${active && kind === "fan" ? "fill-current" : ""}`} />{label}{showCount && count > 0 ? ` \u00b7 ${count}` : ""}
      </Button>
    );
  };
  return <>{order.map(one)}</>;
}
